import { useAuthStore } from "@features/authentication/stores/authStore";
import onboardingService from "@features/onboarding/services/onboardingService";
import type { OnboardingUser, User } from "@fitties/shared";
import logger from "@logger";

import useCurrentUser from "@shared/queries/useCurrentUser";
import QUERY_KEYS from "@shared/queryKeys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const RETRYABLE_SAVE_MESSAGE = "Save timed out. Check your connection and try again.";

function isRetryableOnboardingError(error: unknown): boolean {
  return error instanceof Error && error.message === RETRYABLE_SAVE_MESSAGE;
}

type UpdateOnboardingUserContext = {
  previousUser: User | null | undefined;
};

/**
 * React Query mutation hook for updating onboarding user data.
 *
 * Provides optimistic updates and proper error handling for
 * onboarding user profile updates during the onboarding flow.
 *
 * @example
 * ```tsx
 * const { mutate, isPending, error } = useUpdateOnboardingUser();
 *
 * const handleUpdate = () => {
 *   mutate({ display_name: "John Doe" });
 * };
 * ```
 */
export function useUpdateOnboardingUser() {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.userId);
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: (updates: Partial<OnboardingUser>) => {
      if (!userId) {
        throw new Error("User not authenticated");
      }
      return onboardingService.updateOnboardingUser(userId, updates);
    },

    retry: (failureCount, error) => failureCount < 2 && isRetryableOnboardingError(error),
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),

    onMutate: async (updates): Promise<UpdateOnboardingUserContext> => {
      if (!userId) {
        return { previousUser: undefined };
      }

      const queryKey = [QUERY_KEYS.CURRENT_USER, userId] as const;
      await queryClient.cancelQueries({ queryKey });
      const previousUser = queryClient.getQueryData<User | null>(queryKey);

      queryClient.setQueryData<User | null>(
        queryKey,
        (old) =>
          ({
            uid: userId,
            ...(old ?? user ?? {}),
            ...updates,
          }) as User
      );

      return { previousUser };
    },

    onError: (error, _updates, context) => {
      if (userId && context) {
        queryClient.setQueryData([QUERY_KEYS.CURRENT_USER, userId], context.previousUser);
      }
      logger.error("Failed to update onboarding user:", error);
    },

    onSuccess: (_result, updates) => {
      if (!userId) {
        return;
      }
      const queryKey = [QUERY_KEYS.CURRENT_USER, userId] as const;
      queryClient.setQueryData<User | null>(queryKey, (old) => (old ? ({ ...old, ...updates } as User) : old));
    },
  });
}
